import * as React from "react";
import { defineMessage, FormattedMessage } from "react-intl";
import Section from "./Section";
import { Grid, GridProps, IconButton, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    getConcertoJoinLinkHref,
    getDownloadSource,
    getSipConferenceNumbers,
    getTelConferenceNumbers,
} from "../joinMeetingTools";
import { Link } from "conference/components/tools";
import { Invitation, Meeting } from "../types";
import { isBrowserSupported, useDebugSettings } from "conference/tools";

type Props = {
    meeting: Meeting;
    invitation: Invitation;
};

const JoinMeetingSection = ({ invitation, meeting }: Props) => {
    const debugSettings = useDebugSettings();

    return (
        <Section
            icon="sign-in-alt"
            label={defineMessage({ id: "joinTheMeeting", defaultMessage: "Join meeting options" })}
        >
            <Grid container size={12} style={{ fontSize: useTheme().typography.body1.fontSize }}>
                <Grid {...gridBlockProps}>
                    <Grid size="grow">
                        <strong>
                            <FormattedMessage id="joinWithConcerto" defaultMessage="Join with Concerto client" />
                        </strong>
                        <a style={linkStyle} href={getConcertoJoinLinkHref(meeting, invitation)}>
                            <IconButton size="small" color="primary">
                                <FontAwesomeIcon icon="sign-in-alt" size="sm" />
                            </IconButton>
                        </a>
                    </Grid>
                    {createDownloadLink()}
                </Grid>
                {createTelephoneJoin()}
                {createJoinWithSip()}
                {(isBrowserSupported || (debugSettings.enabled && debugSettings.enableAllBrowsers)) && (
                    <Grid {...gridBlockProps}>
                        <Grid size="grow">
                            <strong>
                                <FormattedMessage id="joinWithWebSectionTitle" defaultMessage="Join with web" />
                            </strong>
                        </Grid>
                        <Grid size="grow">
                            <Link to={`/meeting/join/${meeting.id}`}>
                                <FormattedMessage id="joinWithWebLink" defaultMessage="Click here to join" />
                            </Link>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Section>
    );
};

function createTelephoneJoin(): React.ReactNode {
    const e164Numbers = getTelConferenceNumbers();
    if (!e164Numbers.length) return null;

    return (
        <Grid {...gridBlockProps}>
            <Grid size="grow">
                <strong>
                    <FormattedMessage id="dialIn" defaultMessage="Dial in" />
                </strong>
            </Grid>
            {e164Numbers.map((e164) => {
                return (
                    <Grid size="grow" key={e164}>
                        <span>{e164}</span>
                        <span>
                            <a style={linkStyle} href={`tel:${e164}`}>
                                <IconButton size="small" color="primary">
                                    <FontAwesomeIcon icon="phone" size="sm" />
                                </IconButton>
                            </a>
                        </span>
                    </Grid>
                );
            })}
        </Grid>
    );
}

function createJoinWithSip(): React.ReactNode {
    const sipUris = getSipConferenceNumbers();

    if (!sipUris.length) return null;

    return (
        <Grid {...gridBlockProps}>
            <Grid size="grow">
                <strong>
                    <FormattedMessage id="joinWithSIPURI" defaultMessage="Join with SIP URI" />
                </strong>
            </Grid>
            {sipUris.map((sipUri) => {
                return (
                    <Grid size="grow" key={sipUri}>
                        <span>{sipUri}</span>
                        <span>
                            <a style={linkStyle} href={`sip:${sipUri}`}>
                                <IconButton size="small" color="primary">
                                    <FontAwesomeIcon icon="phone" size="sm" />
                                </IconButton>
                            </a>
                        </span>
                    </Grid>
                );
            })}
        </Grid>
    );
}

function createDownloadLink(): React.ReactNode {
    const downloadSource = getDownloadSource();

    if (!downloadSource) return null;

    return (
        <Grid size="grow">
            <FormattedMessage id="concertoDownloadHint" defaultMessage="Download the Concerto client" />
            <a style={{ paddingLeft: 5 }} href={downloadSource}>
                <IconButton size="small" color="primary">
                    <FontAwesomeIcon icon="download" size="sm" />
                </IconButton>
            </a>
        </Grid>
    );
}

const linkStyle: React.CSSProperties = { paddingLeft: 5 };
const gridBlockProps: GridProps = {
    size: { xs: 12, md: 4 },
    style: {
        paddingTop: 20,
    },
};

export default JoinMeetingSection;
